import React, { useEffect, useState, useCallback } from "react";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { Gift, Users, Sparkles, X } from "lucide-react";

/**
 * NotificationCenter — polls `/api/my/notifications/unseen` on mount +
 * every 30 seconds, then presents each unseen notification as a modal
 * pop-up the customer must acknowledge. Fires the popup when:
 *   - Admin gifts them Apex Rewards ("GIFT")
 *   - A customer uses their referral code and pays ("REFERRAL_EARNED")
 *   - They hit a referral milestone bonus ("REFERRAL_MILESTONE")
 * Each notification includes the amount + a friendly message + (for
 * referrals) the IGN + email of who used their code, exactly as the
 * owner asked.
 */
export default function NotificationCenter() {
  const { user, refresh } = useAuth();
  const [queue, setQueue] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (!user) return;
    try {
      const { data } = await api.get("/my/notifications/unseen");
      if (Array.isArray(data) && data.length) setQueue(data);
    } catch (_e) { /* silent — polling shouldn't spam errors */ }
  }, [user]);

  useEffect(() => {
    if (!user) { setQueue([]); return; }
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [user, load]);

  const dismiss = async () => {
    const [head, ...rest] = queue;
    if (!head) return;
    setBusy(true);
    try {
      await api.post(`/my/notifications/${head.id}/ack`);
      setQueue(rest);
      // Refresh auth so the sidebar balance also updates.
      if (refresh) refresh();
    } catch (_e) {}
    setBusy(false);
  };

  if (!user || queue.length === 0) return null;
  const n = queue[0];
  const KIND_META = {
    GIFT: {
      Icon: Gift,
      title: "You've received a Vault gift!",
      accent: "text-[var(--av-gold)]",
    },
    REFERRAL_EARNED: {
      Icon: Users,
      title: "Your referral code earned you a reward",
      accent: "purple-text",
    },
    REFERRAL_MILESTONE: {
      Icon: Sparkles,
      title: "Referral milestone unlocked!",
      accent: "text-emerald-400",
    },
  };
  const meta = KIND_META[n.kind] || KIND_META.GIFT;
  const Icon = meta.Icon;
  const referredIgn = n.meta?.referred_ign;
  const referredEmail = n.meta?.referred_email;
  const orderId = n.meta?.order_id;
  const remainingCount = queue.length;

  return (
    <div
      className="fixed inset-0 z-[70] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 animate-fadeIn"
      role="dialog"
      aria-modal="true"
      data-testid="notification-modal"
    >
      <div className="vault-card p-6 max-w-md w-full space-y-4 relative"
        data-testid={`notification-${n.kind.toLowerCase()}`}>
        <button
          onClick={dismiss}
          disabled={busy}
          aria-label="Dismiss"
          data-testid="notification-dismiss-x"
          className="absolute top-3 right-3 p-1 rounded-full hover:bg-white/5 text-[var(--av-text-dim)] hover:text-[var(--av-gold)]"
        >
          <X className="w-4 h-4" />
        </button>
        <div className="flex flex-col items-center text-center pt-2">
          <Icon className={`w-12 h-12 mb-3 ${meta.accent}`} strokeWidth={1.5}/>
          <div className="mono-caption text-[var(--av-text-dim)]">
            {n.kind === "GIFT" && "From the Vault Owner"}
            {n.kind === "REFERRAL_EARNED" && "Someone used your code"}
            {n.kind === "REFERRAL_MILESTONE" && "Milestone unlocked"}
          </div>
          <h3 className={`font-serif text-2xl mt-1 ${meta.accent}`}>{meta.title}</h3>
          <div className="mt-4 font-serif text-4xl gold-text" data-testid="notification-amount">
            +£{Number(n.amount_gbp || 0).toFixed(2)}
          </div>
          <p className="text-sm text-[var(--av-text-muted)] mt-4 leading-snug">
            {n.message}
          </p>
          {(referredIgn || referredEmail) && (
            <div className="mt-4 w-full border-t border-[var(--av-gold)]/20 pt-3 text-left">
              <div className="mono-caption text-[var(--av-text-dim)] text-[10px]">Used by</div>
              {referredIgn && (
                <div className="text-sm font-semibold text-[var(--av-gold)]" data-testid="notification-referred-ign">{referredIgn}</div>
              )}
              {referredEmail && (
                <div className="text-xs text-[var(--av-text-dim)] font-mono" data-testid="notification-referred-email">{referredEmail}</div>
              )}
              {orderId && (
                <div className="text-[10px] text-[var(--av-text-dim)] font-mono mt-1">Order {orderId}</div>
              )}
            </div>
          )}
        </div>
        <div className="flex flex-col gap-2 pt-2">
          <button
            onClick={dismiss}
            disabled={busy}
            data-testid="notification-dismiss-btn"
            className="btn-gold w-full disabled:opacity-60"
          >
            {busy ? "…" : (remainingCount > 1 ? `Great — next (${remainingCount - 1} more)` : "Got it")}
          </button>
        </div>
      </div>
    </div>
  );
}
